import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { SupplierBasketService } from '../service/basket-supplier.service';
import { catchError, concatMap, exhaustMap, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    BottomSheetService,
    IFileResponse,
    NotificationMessageLevel,
    NotificationService,
    UploadBottomSheetResult,
} from '@lib/core';
import {
    AddAttachmentsRequest,
    Attachments,
    Basket,
    BasketComment,
    BasketItem,
    BasketItemRequest,
    DeleteAttachmentFileRequest,
    SupplierBasket,
    SupplierNote,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { IFileUploadMaxSize } from '@lib/upload-zone/ui';
import { BasketDialogService } from '../service/basket-dialog.service';
import { BasketSupplierDeliveryConditionsDataService } from '../service/basket-supplier-delivery-conditions-data.service';
import { DeliveryCondition } from '@lib/delivery-conditions';
import { BasketItemRefreshDataService } from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/data-access';
import { PaymentTerm, PaymentTermsDataService, PaymentTermService } from '@lib/payment-terms';
import {
    FrameContractSelectionDataService,
    FrameContractSelectionDialogService,
    FrameContractSelectionResponseDto,
    FrameContractSelection,
} from '@lib/frame-contract-selection';

interface ISupplierProcurementContext {
    readonly supplierId: string;
    readonly companyCodeId: string | null;
}

interface IPaymentTermSelectionUpdate {
    readonly paymentTermId: number;
    readonly showNotification?: boolean;
}

export interface BasketSupplierState {
    supplierBasket: SupplierBasket | null;
    deliveryCondition?: DeliveryCondition | null;
    basketItems: BasketItem[];
    uploadMaxSize: IFileUploadMaxSize | null;
    isLoading?: boolean;
    isPaymentTermRequired?: boolean;
    paymentTerms: PaymentTerm[];
    frameContracts: FrameContractSelectionResponseDto[] | null;
    isGeneralAgreementRequired: boolean;
}

export const DEFAULT_BASKET_SUPPLIER_STATE = (): BasketSupplierState => ({
    supplierBasket: null,
    deliveryCondition: null,
    basketItems: [],
    uploadMaxSize: null,
    isLoading: false,
    isPaymentTermRequired: false,
    paymentTerms: [],
    frameContracts: [],
    isGeneralAgreementRequired: false,
});

@Injectable()
export class BasketSupplierStore extends ComponentStore<BasketSupplierState> {
    private readonly notificationService = inject(NotificationService);
    private lastAutoSelectedPaymentTermKey: string | null = null;
    private lastAutoSelectedFrameContractKey: string | null = null;

    supplierBasketService = inject(SupplierBasketService);
    bottomSheetService = inject(BottomSheetService);
    basketDialogService = inject(BasketDialogService);
    basketSupplierDeliveryConditionsDataService = inject(BasketSupplierDeliveryConditionsDataService);
    basketItemRefreshDataService = inject(BasketItemRefreshDataService);
    demandPaymentTermDataService = inject(PaymentTermsDataService);
    paymentTermService = inject(PaymentTermService);
    frameContractSelectionDataService = inject(FrameContractSelectionDataService);
    frameContractSelectionDialogService = inject(FrameContractSelectionDialogService);

    constructor() {
        super(DEFAULT_BASKET_SUPPLIER_STATE());

        this.basketItemRefreshDataService.updateItemAmountSubject.subscribe((basketItemUpdatedRefreshData) => {
            if (this.get().supplierBasket?.id !== basketItemUpdatedRefreshData.supplierBasketId) {
                return;
            }
            const foundSupplierBasket = this.findSupplierBasket(
                basketItemUpdatedRefreshData.basket,
                basketItemUpdatedRefreshData.supplierBasketId,
            );

            if (foundSupplierBasket.paymentTerm) {
                foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
            }

            /**
             * If there is a change in number of items there is possibility that we need to add or remove delivery cost
             * which appears as another item in basket
             */
            if (this.get().basketItems.length === foundSupplierBasket.basketItems.length) {
                this.addBasketSupplier(foundSupplierBasket);
            } else {
                this.basketItemRefreshDataService.deliveryCostChanged.next(basketItemUpdatedRefreshData.basket);
            }
        });

        this.basketItemRefreshDataService.deleteItemFromHeaderSubject.subscribe(() => {
            const supplierBasketId = this.get().supplierBasket?.id;
            if (supplierBasketId) {
                this.loadBasketSupplier$(supplierBasketId);
            }
        });
    }

    readonly supplierBasket = this.select((state) => state.supplierBasket);
    readonly isLoading$ = this.select((state) => state.isLoading);
    readonly basketItems = this.select((state) => state.basketItems);
    readonly deliveryCondition = this.select((state) => state.deliveryCondition);
    readonly paymentTerms = this.select((state) => state.paymentTerms);
    readonly isPaymentTermRequired = this.select((state) => state.isPaymentTermRequired);
    readonly frameContracts = this.select((state) => state.frameContracts);
    readonly isGeneralAgreementRequired = this.select((state) => state.isGeneralAgreementRequired);

    addBasketSupplier = this.updater((state: BasketSupplierState, supplierBasket: SupplierBasket | null) => {
        return {
            ...state,
            supplierBasket: this.preserveSupplierNote(state.supplierBasket, supplierBasket),
        };
    });

    addBasketSupplierWithItems = this.updater((state: BasketSupplierState, supplierBasket: SupplierBasket | null) => {
        const newBasketItems = supplierBasket?.basketItems || [];

        return {
            ...state,
            supplierBasket: this.preserveSupplierNote(state.supplierBasket, supplierBasket),
            basketItems: [...newBasketItems],
        };
    });

    // Supplier note is loaded via a separate endpoint (getSupplierNote$) and is not part of basket
    // mutation responses. Carry the previously loaded note over so updates like payment-term edits
    // do not blank out the order hint banner.
    private preserveSupplierNote(
        previous: SupplierBasket | null,
        next: SupplierBasket | null,
    ): SupplierBasket | null {
        if (!next || next.supplierNote || !previous?.supplierNote) {
            return next;
        }
        return { ...next, supplierNote: previous.supplierNote };
    }

    readonly setIsLoading = this.updater((state, isLoading: boolean) => ({
        ...state,
        isLoading,
    }));

    addSupplierNotes = this.updater((state: BasketSupplierState, supplierNote: SupplierNote) => {
        if (state.supplierBasket) {
            const updatedSupplierBasket: SupplierBasket = { ...state.supplierBasket, supplierNote: supplierNote };

            return {
                ...state,
                supplierBasket: updatedSupplierBasket,
            };
        }

        return state;
    });

    addUploadMaxSize = this.updater((state: BasketSupplierState, uploadMaxSize: IFileUploadMaxSize) => {
        return {
            ...state,
            uploadMaxSize,
        };
    });

    addDeliveryCondition = this.updater((state: BasketSupplierState, deliveryCondition: DeliveryCondition) => {
        return {
            ...state,
            deliveryCondition,
        };
    });

    deleteDeliveryCondition = this.updater((state: BasketSupplierState) => {
        return {
            ...state,
            deliveryCondition: null,
        };
    });

    addPaymentTerms = this.updater((state: BasketSupplierState, paymentTerms: PaymentTerm[]) => {
        return {
            ...state,
            paymentTerms: [...paymentTerms],
        };
    });

    readonly setIsPaymentTermRequired = this.updater((state, isPaymentTermRequired: boolean) => ({
        ...state,
        isPaymentTermRequired,
    }));

    readonly setIsGeneralAgreementRequired = this.updater((state, isGeneralAgreementRequired: boolean) => ({
        ...state,
        isGeneralAgreementRequired,
    }));

    readonly resetSupplierProcurementData = this.updater((state: BasketSupplierState) => ({
        ...state,
        isPaymentTermRequired: false,
        paymentTerms: [],
        frameContracts: [],
        isGeneralAgreementRequired: false,
    }));

    addFrameContracts = this.updater(
        (state: BasketSupplierState, frameContracts: FrameContractSelectionResponseDto[]) => {
            return {
                ...state,
                frameContracts: [...frameContracts],
            };
        },
    );

    readonly loadPaymentTerms$ = this.effect((origin$: Observable<ISupplierProcurementContext>) =>
        origin$.pipe(
            switchMap(({ supplierId, companyCodeId }) => {
                if (!companyCodeId) {
                    this.addPaymentTerms([]);
                    return of([]);
                }

                this.setIsLoading(true);
                return this.demandPaymentTermDataService.getAllPaymentTermsIgnoreCache(supplierId, companyCodeId).pipe(
                    tap((responseTerms: PaymentTerm[]) => {
                        this.addPaymentTerms(responseTerms);
                        this.tryAutoSelectPaymentTerm();
                    }),
                    catchError((error) => {
                        this.addPaymentTerms([]);
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly loadIsPaymentTermRequired$ = this.effect((origin$: Observable<ISupplierProcurementContext>) =>
        origin$.pipe(
            switchMap(({ supplierId, companyCodeId }) => {
                if (!companyCodeId) {
                    this.setIsPaymentTermRequired(false);
                    return of(false);
                }

                this.setIsLoading(true);
                return this.demandPaymentTermDataService.getIsPaymentTermRequired(supplierId, companyCodeId).pipe(
                    tap((isPaymentTermRequired: boolean) => {
                        this.setIsPaymentTermRequired(isPaymentTermRequired);
                        this.tryAutoSelectPaymentTerm();
                    }),
                    catchError((error) => {
                        this.setIsPaymentTermRequired(false);
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly openPaymentTermDialog$ = this.effect((origin$: Observable<void>) =>
        origin$.pipe(
            switchMap(() =>
                        this.paymentTermService
                            .selectPaymentTerm(this.get().supplierBasket?.supplier!.id, true, this.get().paymentTerms)
                    .pipe(
                        tap((selectedPaymentTerm: PaymentTerm) => {
                            this.updatePaymentTerm$(selectedPaymentTerm.id);
                        }),
                        catchError((error) => {
                            return of(error);
                        }),
                        finalize(() => {
                            this.setIsLoading(false);
                        }),
                    ),
            ),
        ),
    );

    readonly updatePaymentTerm$ = this.effect((origin$: Observable<number | IPaymentTermSelectionUpdate>) =>
        origin$.pipe(
            exhaustMap((request) => {
                const paymentTermId = typeof request === 'number' ? request : request.paymentTermId;
                const showNotification = typeof request === 'number' ? true : request.showNotification !== false;
                this.setIsLoading(true);
                return this.supplierBasketService.updatePaymentTerm(paymentTermId, this.get().supplierBasket!.id).pipe(
                    tap((basket) => {
                        const foundSupplierBasket = this.findSupplierBasket(basket, this.get().supplierBasket!.id);

                        foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);

                        this.addBasketSupplier(foundSupplierBasket);

                        if (showNotification) {
                            const message = marker('Zahlungsbedingung erfolgreich aktualisiert');
                            this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                        }
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly deletePaymentTerm$ = this.effect((origin$: Observable<void>) =>
        origin$.pipe(
            exhaustMap(() => {
                this.setIsLoading(true);
                return this.supplierBasketService.deletePaymentTerm(this.get().supplierBasket!.id).pipe(
                    tap((basket) => {
                        const foundSupplierBasket = this.findSupplierBasket(basket, this.get().supplierBasket!.id);

                        this.addBasketSupplier(foundSupplierBasket);

                        const message = marker('Zahlungsbedingung erfolgreich aktualisiert');
                        this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly loadFrameContracts$ = this.effect((origin$: Observable<ISupplierProcurementContext>) =>
        origin$.pipe(
            switchMap(({ supplierId, companyCodeId }) => {
                if (!companyCodeId) {
                    this.addFrameContracts([]);
                    return of([]);
                }

                this.setIsLoading(true);
                return this.frameContractSelectionDataService.getFrameContracts(supplierId, companyCodeId).pipe(
                    tap((frameContractSelectionData: FrameContractSelectionResponseDto[]) => {
                        this.addFrameContracts(frameContractSelectionData);
                        this.resetStaleFrameContractSelection(frameContractSelectionData);
                        this.tryAutoSelectFrameContract();
                    }),
                    catchError((error) => {
                        this.addFrameContracts([]);
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly loadIsGeneralAgreementRequired$ = this.effect((origin$: Observable<ISupplierProcurementContext>) =>
        origin$.pipe(
            switchMap(({ supplierId, companyCodeId }) => {
                if (!companyCodeId) {
                    this.setIsGeneralAgreementRequired(false);
                    return of(false);
                }

                this.setIsLoading(true);
                return this.frameContractSelectionDataService
                    .getIsGeneralAgreementRequired(supplierId, companyCodeId)
                    .pipe(
                        tap((required: boolean) => {
                            this.setIsGeneralAgreementRequired(required);
                            this.tryAutoSelectFrameContract();
                        }),
                        catchError((error) => {
                            this.setIsGeneralAgreementRequired(false);
                            return of(error);
                        }),
                        finalize(() => {
                            this.setIsLoading(false);
                        }),
                    );
            }),
        ),
    );

    readonly openFrameContractSelectionDialog$ = this.effect((origin$: Observable<void>) =>
        origin$.pipe(
            exhaustMap(() => {
                const supplierBasket = this.get().supplierBasket;
                const supplierBasketId = supplierBasket?.id;

                return this.frameContractSelectionDialogService
                    .openFrameContractSelectionDialog(this.get().frameContracts)
                    .pipe(
                        tap((frameContractSelection: FrameContractSelection) => {
                            if (frameContractSelection && supplierBasketId) {
                                const frameContractId = frameContractSelection.id;
                                this.updateFrameContractSelection$({ supplierBasketId, frameContractId });
                            }
                        }),
                        catchError((error) => {
                            return of(error);
                        }),
                    );
            }),
        ),
    );

    readonly updateFrameContractSelection$ = this.effect(
        (origin$: Observable<{ supplierBasketId: string; frameContractId: number }>) =>
            origin$.pipe(
                exhaustMap(({ supplierBasketId, frameContractId }) => {
                    this.setIsLoading(true);
                    return this.supplierBasketService
                        .updateFrameContracts(supplierBasketId, { frameContractId: frameContractId })
                        .pipe(
                            tap((basket) => {
                                const foundSupplierBasket = this.findSupplierBasket(basket, supplierBasketId);

                                this.addBasketSupplier(foundSupplierBasket);

                                if (foundSupplierBasket.paymentTerm) {
                                    foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                                }

                                const message = marker('Rahmenvertrag erfolgreich aktualisiert');
                                this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                            }),
                            catchError((error) => {
                                return of(error);
                            }),
                            finalize(() => {
                                this.setIsLoading(false);
                            }),
                        );
                }),
            ),
    );

    readonly deleteFrameContractSelection$ = this.effect((origin$: Observable<void>) =>
        origin$.pipe(
            switchMap(() => {
                const supplierBasket = this.get().supplierBasket;
                const supplierBasketId = supplierBasket?.id;
                this.setIsLoading(true);
                return this.supplierBasketService.deleteFrameContracts(supplierBasketId!).pipe(
                    tap((basket) => {
                        const foundSupplierBasket = this.findSupplierBasket(basket, supplierBasketId!);

                        if (foundSupplierBasket.paymentTerm) {
                            foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                        }

                        this.addBasketSupplier(foundSupplierBasket);

                        const message = marker('Rahmenvertrag erfolgreich aktualisiert');
                        this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly loadBasketSupplier$ = this.effect((origin$: Observable<string>) =>
        origin$.pipe(
            switchMap((id) => {
                this.setIsLoading(true);
                return this.supplierBasketService.getBasket().pipe(
                    tap((basket: Basket) => {
                        const foundSupplierBasket = this.findSupplierBasket(basket, id);

                        this.loadSupplierProcurementData(foundSupplierBasket.supplier.id, basket.companyCodeId);

                        if (foundSupplierBasket.paymentTerm) {
                            foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                        }

                        this.addBasketSupplier(foundSupplierBasket);

                        this.getSupplierNote$(foundSupplierBasket.supplier.id);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly loadBasketSupplierWithItems$ = this.effect((origin$: Observable<string>) =>
        origin$.pipe(
            switchMap((id) => {
                this.setIsLoading(true);
                return this.supplierBasketService.getBasket().pipe(
                    tap((basket: Basket) => {
                        const foundSupplierBasket = this.findSupplierBasket(basket, id);

                        this.loadSupplierProcurementData(foundSupplierBasket.supplier.id, basket.companyCodeId);

                        if (foundSupplierBasket.paymentTerm) {
                            foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                        }

                        this.addBasketSupplierWithItems(foundSupplierBasket);

                        this.getSupplierNote$(foundSupplierBasket.supplier.id);

                        this.getDeliveryCondition(foundSupplierBasket.supplier.id);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    private loadSupplierProcurementData(supplierId: string, companyCodeId: string | null): void {
        // Reset auto-select dedup keys on every reload cycle: they only coordinate the two
        // parallel effects within a single load and must not survive a company-code switch
        // (otherwise the same single payment term / frame contract is not re-selected).
        this.lastAutoSelectedPaymentTermKey = null;
        this.lastAutoSelectedFrameContractKey = null;

        if (!companyCodeId) {
            this.resetSupplierProcurementData();
            return;
        }

        const procurementContext: ISupplierProcurementContext = {
            supplierId,
            companyCodeId,
        };

        this.loadPaymentTerms$(procurementContext);
        this.loadIsPaymentTermRequired$(procurementContext);
        this.loadFrameContracts$(procurementContext);
        this.loadIsGeneralAgreementRequired$(procurementContext);
    }

    private resetStaleFrameContractSelection(frameContracts: FrameContractSelectionResponseDto[]): void {
        const supplierBasket = this.get().supplierBasket;
        const currentSelection = supplierBasket?.frameContract;
        if (!supplierBasket || !currentSelection) {
            return;
        }

        const stillAvailable = frameContracts.some((contract) => contract.id === currentSelection.id);
        if (stillAvailable) {
            return;
        }

        const supplierBasketId = supplierBasket.id;
        this.supplierBasketService.deleteFrameContracts(supplierBasketId).pipe(
            tap((basket) => {
                const foundSupplierBasket = this.findSupplierBasket(basket, supplierBasketId);
                if (foundSupplierBasket?.paymentTerm) {
                    foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                }
                this.addBasketSupplier(foundSupplierBasket);
                const message = marker(
                    'Rahmenvertrag wurde zurückgesetzt - der gewählte Vertrag ist im aktiven Buchungskreis nicht verfügbar.',
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.INFO);
            }),
            catchError((error) => of(error)),
        ).subscribe();
    }

    private tryAutoSelectFrameContract(): void {
        const supplierBasket = this.get().supplierBasket;
        const frameContracts = this.get().frameContracts ?? [];
        const isRequired = this.get().isGeneralAgreementRequired;

        if (!supplierBasket || supplierBasket.frameContract || !isRequired || frameContracts.length !== 1) {
            this.lastAutoSelectedFrameContractKey = null;
            return;
        }

        const candidate = frameContracts[0];
        const autoKey = `${supplierBasket.id}:${candidate.id}`;
        if (this.lastAutoSelectedFrameContractKey === autoKey) {
            return;
        }

        this.lastAutoSelectedFrameContractKey = autoKey;
        this.updateFrameContractSelection$({ supplierBasketId: supplierBasket.id, frameContractId: candidate.id });
    }

    private tryAutoSelectPaymentTerm(): void {
        const supplierBasket = this.get().supplierBasket;
        const paymentTerms = this.get().paymentTerms;
        const isPaymentTermRequired = this.get().isPaymentTermRequired;

        const autoSelectedPaymentTerm =
            supplierBasket && !supplierBasket.paymentTerm && isPaymentTermRequired && paymentTerms.length === 1
                ? paymentTerms[0]
                : null;

        if (!supplierBasket || !autoSelectedPaymentTerm) {
            this.lastAutoSelectedPaymentTermKey = null;
            return;
        }

        const autoSelectedPaymentTermKey = `${supplierBasket.id}:${autoSelectedPaymentTerm.id}`;
        if (this.lastAutoSelectedPaymentTermKey === autoSelectedPaymentTermKey) {
            return;
        }

        this.lastAutoSelectedPaymentTermKey = autoSelectedPaymentTermKey;
        this.updatePaymentTerm$({
            paymentTermId: autoSelectedPaymentTerm.id,
            showNotification: false,
        });
    }

    readonly getSupplierNote$ = this.effect((origin$: Observable<string>) =>
        origin$.pipe(
            concatMap((supplierId) => {
                this.setIsLoading(true);
                return this.supplierBasketService.getSupplierNote(supplierId).pipe(
                    tap((supplierNote: SupplierNote) => {
                        this.addSupplierNotes(supplierNote);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly deleteBasketItem$ = this.effect((origin$: Observable<BasketItemRequest>) =>
        origin$.pipe(
            exhaustMap((basketItemRequest) => {
                this.setIsLoading(true);
                // EPROC-12041: removing the last position with a site falls back to the personal
                // default — the recipient's when "Bestellen im Namen von" is active.
                const targetUserId = this.basketItemRefreshDataService.orderOnBehalfTargetUserId;
                return this.supplierBasketService.deleteBasketItem(basketItemRequest, targetUserId).pipe(
                    tap((basket) => {
                        const message = marker('Produkt erfolgreich aus dem Warenkorb entfernt');
                        this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);

                        const foundSupplierBasket = this.findSupplierBasket(basket, basketItemRequest.supplierBasketId);

                        if (!foundSupplierBasket) {
                            this.addDeliveryCondition(null);
                        }

                        if (foundSupplierBasket && foundSupplierBasket.paymentTerm) {
                            foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                        }

                        this.addBasketSupplierWithItems(foundSupplierBasket);

                        this.basketItemRefreshDataService.deleteItemSubject.next(basket);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    openUploadFileDialog$(supplierBasketId: string) {
        this.openUploadFileDialog(supplierBasketId);
    }

    readonly addAttachments$ = this.effect((origin$: Observable<AddAttachmentsRequest>) =>
        origin$.pipe(
            exhaustMap((attachments) => {
                this.setIsLoading(true);
                return this.supplierBasketService.addAttachments(attachments).pipe(
                    tap(() => {
                        this.loadBasketSupplier$(attachments.supplierBasketId);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly updateSupplierComments$ = this.effect((origin$: Observable<BasketComment>) =>
        origin$.pipe(
            exhaustMap((basketComment) => {
                this.setIsLoading(true);
                return this.supplierBasketService
                    .updateSupplierComment(basketComment.comment, basketComment.supplierBasketId)
                    .pipe(
                        tap((basket) => {
                            const foundSupplierBasket = this.findSupplierBasket(basket, basketComment.supplierBasketId);

                            if (foundSupplierBasket.paymentTerm) {
                                foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                            }

                            this.addBasketSupplier(foundSupplierBasket);
                            this.basketItemRefreshDataService.supplierBasketCommentsChangedSubject.next(basket);

                            // TODO check if this is needed or we need to call this from somewhere else
                            //  we are calling this all the time and in lot of effects, CHECK THIS
                            // this.checkExistenceInWarehouse(basket.supplierBaskets);
                            // this.getQuantityInStock(basket.supplierBaskets);
                            // this.getSupplierBasketId(basket.supplierBaskets);
                        }),
                        catchError((error) => {
                            return of(error);
                        }),
                        finalize(() => {
                            this.setIsLoading(false);
                        }),
                    );
            }),
        ),
    );

    readonly deleteAttachmentFile$ = this.effect((origin$: Observable<DeleteAttachmentFileRequest>) =>
        origin$.pipe(
            exhaustMap((attachmentFile) => {
                this.setIsLoading(true);
                return this.supplierBasketService.deleteAttachmentFile(attachmentFile).pipe(
                    tap(() => {
                        this.loadBasketSupplier$(attachmentFile.supplierBasketId);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly updatePersonComments$ = this.effect((origin$: Observable<BasketComment>) =>
        origin$.pipe(
            exhaustMap((basketComment) => {
                this.setIsLoading(true);
                return this.supplierBasketService
                    .updatePersonComment(basketComment.comment, basketComment.supplierBasketId)
                    .pipe(
                        tap((basket) => {
                            const foundSupplierBasket = this.findSupplierBasket(basket, basketComment.supplierBasketId);

                            if (foundSupplierBasket.paymentTerm) {
                                foundSupplierBasket.paymentTerm = new PaymentTerm(foundSupplierBasket.paymentTerm);
                            }

                            this.addBasketSupplier(foundSupplierBasket);
                            this.basketItemRefreshDataService.supplierBasketCommentsChangedSubject.next(basket);
                        }),
                        catchError((error) => {
                            return of(error);
                        }),
                        finalize(() => {
                            this.setIsLoading(false);
                        }),
                    );
            }),
        ),
    );

    readonly getMaxUploadSize$ = this.effect((origin$) =>
        origin$.pipe(
            exhaustMap(() => {
                this.setIsLoading(true);
                return this.supplierBasketService.getMaxUploadSize().pipe(
                    tap((maxSize) => {
                        this.addUploadMaxSize(maxSize);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    getDeliveryCondition(supplierId: string) {
        this.basketSupplierDeliveryConditionsDataService
            .getConditionFor(supplierId)
            .subscribe((deliveryConditionResponse: DeliveryCondition) => {
                this.addDeliveryCondition(deliveryConditionResponse);
            });
    }

    readonly saveDeliveryCondition$ = this.effect((origin$: Observable<DeliveryCondition>) =>
        origin$.pipe(
            exhaustMap((newDeliveryCondition) => {
                const supplierBasket = this.get().supplierBasket;
                const supplierId = supplierBasket?.supplier?.id;
                return this.basketSupplierDeliveryConditionsDataService
                    .update(supplierId!, newDeliveryCondition.value.id)
                    .pipe(
                        tap((deliveryConditionResponse: DeliveryCondition) => {
                            this.addDeliveryCondition(deliveryConditionResponse);

                            const message = marker('Lieferbedingung gespeichert');
                            this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                        }),
                        catchError((error) => {
                            return of(error);
                        }),
                    );
            }),
        ),
    );

    readonly deleteDeliveryCondition$ = this.effect((origin$: Observable<void>) =>
        origin$.pipe(
            exhaustMap(() => {
                const supplierBasket = this.get().supplierBasket;
                const supplierId = supplierBasket?.supplier?.id;
                return this.basketSupplierDeliveryConditionsDataService.deleteCondition(supplierId!).pipe(
                    tap(() => {
                        this.deleteDeliveryCondition();
                        const message = marker('Lieferbedingung entfernt');
                        this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                );
            }),
        ),
    );

    private openUploadFileDialog(supplierBasketId: string): void {
        const maxFileSize = this.get().uploadMaxSize;

        if (maxFileSize) {
            this.bottomSheetService
                .upload({ uploadInternalPossible: false, multiple: true })
                ?.afterDismissed()
                .subscribe(async (result?: UploadBottomSheetResult) => {
                    if (result) {
                        const addAttachmentsRequest = {
                            supplierBasketId: supplierBasketId,
                            attachments: result.formData,
                        };
                        if (result !== undefined) {
                            this.addAttachments$(addAttachmentsRequest);
                        }
                    }
                });
        }
    }

    public findSupplierBasket(basket: Basket, id: string): SupplierBasket {
        let foundSupplierBasket = null;

        basket?.supplierBaskets.forEach((supplierBasket) => {
            if (supplierBasket.id === id) {
                foundSupplierBasket = {
                    ...supplierBasket,
                    attachments: this.mapAttachmentsToFileResponse(supplierBasket.attachments),
                };
            }
        });

        return foundSupplierBasket;
    }

    private mapAttachmentsToFileResponse(attachments: (Attachments | IFileResponse)[] = []): IFileResponse[] {
        return attachments.map((a) => {
            if ('fileName' in a) {
                // It's already an IFileResponse
                return a as IFileResponse;
            } else {
                // It's an Attachments
                return {
                    id: a.id,
                    fileName: a.name,
                    fileSize: a.size,
                    fileType: a.type,
                    fileLink: a.link,
                    internal: false,
                };
            }
        });
    }
}
